import { deleteOperationalMessages, loadOperationalWorkspace, type OperationalWorkspaceSnapshot } from "@/core/services/operational-ai.service";
import type { OperationalMessage } from "@shared/operational-ai/operational-ai.types";
import { showToast } from "@/core/utils/ui";
import { OperationalChatController } from "./chat.controller";
import {
  emitQuetzalitoDataUpdated,
  getActiveWorkspaceId,
  createConversationSession,
  getActiveConversationId,
  getConversationSessions,
  LEGACY_CONVERSATION_ID,
  removeConversationSession,
  setActiveConversationId,
  updateConversationSession,
  type QuetzalitoConversationSession,
} from "./chat.state";
import "./panel.css";

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[character] ?? character);
}

function formatConversationDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Fecha no disponible";
  return new Intl.DateTimeFormat("es-GT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(date);
}

export class QuetzalitoPanel {
  private readonly container: HTMLElement;
  private readonly chat: OperationalChatController;
  private snapshot: OperationalWorkspaceSnapshot | null = null;
  private loadedWorkspaceId: string | null = null;
  private processing = false;
  private loading = false;
  private open = false;
  private closeTimer: number | null = null;
  private activeConversationId = LEGACY_CONVERSATION_ID;
  private conversationStartedAt: string | null = null;
  private conversationEndedAt: string | null = null;
  private historyOpen = false;

  constructor(private readonly onVisibilityChange: (open: boolean) => void) {
    this.container = document.createElement("div");
    this.container.id = "quetzalito-panel";
    this.container.className = "qp-quetzalito-panel opai-chat-gradient hidden";
    document.body.appendChild(this.container);
    this.chat = new OperationalChatController(this.container, () => this.processing);
    this.bindDelegatedEvents();
  }

  isOpen(): boolean {
    return this.open;
  }

  async toggle(): Promise<void> {
    if (this.open) this.hide();
    else await this.show();
  }

  async show(): Promise<void> {
    if (this.open) return;
    this.open = true;
    if (this.closeTimer !== null) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.container.classList.remove("is-closing");
    this.chat.mascot.openChat();
    this.render();
    this.container.classList.remove("hidden");
    this.onVisibilityChange(true);
    void this.container.offsetWidth;
    requestAnimationFrame(() => {
      if (this.open) this.container.classList.add("is-active");
    });
    const workspaceId = getActiveWorkspaceId();
    if (!workspaceId) { this.render(); return; }
    this.activeConversationId = getActiveConversationId(workspaceId);
    this.syncConversationWindow(workspaceId);
    if (workspaceId === this.loadedWorkspaceId && this.snapshot) { this.render(); return; }
    this.loading = true;
    this.render();
    try {
      this.snapshot = await loadOperationalWorkspace(workspaceId);
      this.loadedWorkspaceId = workspaceId;
      this.chat.reset();
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo cargar la conversación.", "error");
      this.snapshot = null;
    } finally {
      this.loading = false;
      this.render();
    }
  }

  hide(): void {
    if (!this.open) return;
    this.open = false;
    this.chat.mascot.closeChat();
    this.container.classList.add("is-closing");
    requestAnimationFrame(() => this.container.classList.remove("is-active"));
    this.closeTimer = window.setTimeout(() => {
      if (!this.open) {
        this.container.classList.add("hidden");
        this.container.classList.remove("is-closing");
        this.onVisibilityChange(false);
      }
      this.closeTimer = null;
    }, 430);
  }

  async startNewConversation(): Promise<void> {
    await this.show();
    if (!this.snapshot) return;
    const session = createConversationSession(this.snapshot.profile.id);
    this.activeConversationId = session.id;
    this.historyOpen = false;
    this.syncConversationWindow(this.snapshot.profile.id);
    this.chat.reset();
    this.render();
    this.focusComposer();
  }

  async showConversationHistory(): Promise<void> {
    await this.show();
    if (!this.snapshot) return;
    this.historyOpen = true;
    this.render();
  }

  private bindDelegatedEvents(): void {
    this.container.addEventListener("click", (event) => void this.handleAction(event));
    this.container.addEventListener("focusout", (event) => this.handleComposerBlur(event));
    this.container.addEventListener("input", (event) => this.handleComposerInput(event));
    this.container.addEventListener("keydown", (event) => this.handleComposerKeydown(event as KeyboardEvent));
    this.container.addEventListener("change", (event) => this.handleFileInputChange(event));
  }

  private render(): void {
    const morphHead = `<span class="qp-quetzalito-panel__morph-head" aria-hidden="true">${this.chat.mascot.render("header")}</span>`;
    const header = `${morphHead}<div class="flex items-center justify-between px-5 py-3 opai-chat-header">
        <div class="flex items-center gap-3 min-w-0">
          <div class="flex h-10 w-11 items-center justify-center shrink-0">${this.chat.mascot.render("header")}</div>
          <div class="flex flex-col min-w-0">
            <h2 class="text-base font-black tracking-wide text-white leading-tight">Quetzalito</h2>
          </div>
        </div>
        <button data-action="close" class="qp-quetzalito-panel__close" aria-label="Cerrar chat"><i class="ph-bold ph-x text-lg"></i></button>
      </div>`;

    if (this.loading) {
      this.container.innerHTML = `${header}<div class="qp-quetzalito-panel__loading"><i class="ph ph-circle-notch animate-spin text-lg" aria-hidden="true"></i>Cargando conversación…</div>`;
      return;
    }
    if (!this.snapshot) {
      this.container.innerHTML = `${header}<div class="qp-quetzalito-panel__empty">
        <p class="qp-quetzalito-panel__empty-text">Aún no tengo una configuración operativa abierta. Entra a la consola y elige o crea un producto para que empecemos a conversar.</p>
        <a href="/admin-quetzalbot" data-link class="qp-quetzalito-panel__empty-link"><i class="ph-bold ph-arrow-right"></i>Ir a la consola</a>
      </div>`;
      return;
    }
    if (this.historyOpen) {
      this.container.innerHTML = header + this.renderHistoryDrawer();
      return;
    }
    const visibleMessages = this.messagesForWindow(this.conversationStartedAt, this.conversationEndedAt);
    const newConversationMessage = this.conversationStartedAt
      ? "Nueva conversación lista. Conservo el conocimiento operativo del negocio, pero este hilo empieza limpio. ¿En qué trabajamos ahora?"
      : "";
    this.container.innerHTML = header + this.chat.renderPane(visibleMessages, newConversationMessage);
    this.chat.dictation.refreshUI();
    this.chat.updateSendButtonState();
    this.chat.mascot.scheduleSleep();
    this.chat.attachInteractions();
    requestAnimationFrame(() => this.chat.scrollMessages());
  }

  private syncConversationWindow(workspaceId: string): void {
    const sessions = getConversationSessions(workspaceId);
    const index = sessions.findIndex((session) => session.id === this.activeConversationId);
    if (index < 0) {
      this.activeConversationId = LEGACY_CONVERSATION_ID;
      this.conversationStartedAt = null;
      this.conversationEndedAt = sessions[0]?.startedAt ?? null;
      return;
    }
    this.conversationStartedAt = sessions[index].startedAt;
    this.conversationEndedAt = sessions[index + 1]?.startedAt ?? null;
  }

  private messagesForWindow(startedAt: string | null, endedAt: string | null): OperationalMessage[] {
    return (this.snapshot?.messages ?? []).filter((message) => {
      if (startedAt && message.createdAt < startedAt) return false;
      return !endedAt || message.createdAt < endedAt;
    });
  }

  private messagesForSession(sessionId: string): OperationalMessage[] {
    if (!this.snapshot) return [];
    const sessions = getConversationSessions(this.snapshot.profile.id);
    const index = sessions.findIndex((session) => session.id === sessionId);
    if (sessionId === LEGACY_CONVERSATION_ID || index < 0) return this.messagesForWindow(null, sessions[0]?.startedAt ?? null);
    return this.messagesForWindow(sessions[index].startedAt, sessions[index + 1]?.startedAt ?? null);
  }

  private renderHistoryDrawer(): string {
    if (!this.snapshot) return "";
    const sessions = getConversationSessions(this.snapshot.profile.id);
    const legacyMessages = this.messagesForSession(LEGACY_CONVERSATION_ID);
    const legacySession: QuetzalitoConversationSession = {
      id: LEGACY_CONVERSATION_ID,
      title: "Conversación inicial",
      startedAt: legacyMessages[0]?.createdAt ?? this.snapshot.profile.createdAt,
      pinned: false,
    };
    const entries = [...sessions, ...(legacyMessages.length > 0 || sessions.length === 0 ? [legacySession] : [])]
      .sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.startedAt.localeCompare(a.startedAt));
    const cards = entries.map((session) => {
      const messages = this.messagesForSession(session.id);
      const preview = messages.at(-1)?.text.trim() || "Conversación sin mensajes todavía.";
      const active = session.id === this.activeConversationId;
      const editable = session.id !== LEGACY_CONVERSATION_ID;
      return `<article class="qp-quetzalito-history__card${active ? " is-active" : ""}${session.pinned ? " is-pinned" : ""}">
        <button type="button" class="qp-quetzalito-history__open" data-action="history-open" data-session-id="${escapeHtml(session.id)}">
          <span class="qp-quetzalito-history__title">${session.pinned ? '<i class="ph-fill ph-bookmark-simple" aria-hidden="true"></i>' : ""}${escapeHtml(session.title)}</span>
          <span class="qp-quetzalito-history__meta">${formatConversationDate(session.startedAt)} · ${messages.length} ${messages.length === 1 ? "mensaje" : "mensajes"}</span>
          <span class="qp-quetzalito-history__preview">${escapeHtml(preview)}</span>
        </button>
        <div class="qp-quetzalito-history__actions" aria-label="Opciones de ${escapeHtml(session.title)}">
          ${editable ? `<button type="button" data-action="history-rename" data-session-id="${escapeHtml(session.id)}" aria-label="Cambiar nombre" title="Etiquetar"><i class="ph-bold ph-tag"></i></button>
          <button type="button" data-action="history-pin" data-session-id="${escapeHtml(session.id)}" aria-label="${session.pinned ? "Quitar de guardados" : "Guardar conversación"}" title="${session.pinned ? "Quitar de guardados" : "Guardar"}"><i class="ph-${session.pinned ? "fill" : "bold"} ph-bookmark-simple"></i></button>` : ""}
          <button type="button" data-action="history-delete" data-session-id="${escapeHtml(session.id)}" aria-label="Eliminar conversación" title="Eliminar"><i class="ph-bold ph-trash"></i></button>
        </div>
      </article>`;
    }).join("");
    return `<section class="qp-quetzalito-history" aria-label="Historial de conversaciones">
      <div class="qp-quetzalito-history__heading">
        <div class="qp-quetzalito-history__heading-main">
          <button type="button" class="qp-quetzalito-history__back" data-action="history-back" aria-label="Volver a la conversación"><i class="ph-bold ph-arrow-left"></i></button>
          <div><h3>Historial</h3><p>Organiza y retoma tus conversaciones.</p></div>
        </div>
        <button type="button" data-action="history-new"><i class="ph-bold ph-plus"></i>Nueva</button>
      </div>
      <div class="qp-quetzalito-history__list">${cards}</div>
    </section>`;
  }

  private async handleAction(event: Event): Promise<void> {
    const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!target) return;
    const action = target.dataset.action;
    this.chat.mascot.wake();

    if (action === "close") { this.hide(); return; }
    if (action === "history-back") { this.historyOpen = false; this.render(); return; }
    if (action === "history-new") { await this.startNewConversation(); return; }
    if (action === "history-open") { this.openHistoryConversation(target.dataset.sessionId); return; }
    if (action === "history-rename") { this.renameHistoryConversation(target.dataset.sessionId); return; }
    if (action === "history-pin") { this.togglePinnedConversation(target.dataset.sessionId); return; }
    if (action === "history-delete") { await this.deleteHistoryConversation(target.dataset.sessionId); return; }
    if (action === "attach") { this.container.querySelector<HTMLInputElement>("#opai-file-input")?.click(); return; }
    if (action === "remove-file") {
      const index = Number(target.dataset.index);
      const [removedFile] = this.chat.pendingFiles.splice(index, 1);
      if (removedFile) this.chat.releasePreviewUrl(removedFile);
      this.chat.refreshAttachmentPreviews();
      return;
    }
    if (action === "dictate") { this.chat.dictation.toggle(); return; }
    if (action === "send") { await this.sendMessage(); return; }
    if (action === "copy-msg") {
      const message = this.snapshot?.messages.find((m) => m.id === target.dataset.msgId);
      if (message) await this.copyMessageText(message.text);
      return;
    }
    if (action === "edit-msg-trigger") { this.chat.editingMessageId = target.dataset.msgId ?? null; this.render(); return; }
    if (action === "cancel-edit-msg") { this.chat.editingMessageId = null; this.render(); return; }
    if (action === "save-edit-msg") {
      const msgId = target.dataset.msgId;
      if (msgId) await this.saveEditedMessage(msgId);
      return;
    }
    if (action === "img-prev") { this.chat.scrollImageTrack(target.dataset.track ?? "", -1); return; }
    if (action === "img-next") { this.chat.scrollImageTrack(target.dataset.track ?? "", 1); return; }
  }

  private openHistoryConversation(sessionId: string | undefined): void {
    if (!this.snapshot || !sessionId) return;
    this.activeConversationId = sessionId;
    setActiveConversationId(this.snapshot.profile.id, sessionId);
    this.syncConversationWindow(this.snapshot.profile.id);
    this.historyOpen = false;
    this.chat.reset();
    this.render();
    this.focusComposer();
  }

  private renameHistoryConversation(sessionId: string | undefined): void {
    if (!this.snapshot || !sessionId || sessionId === LEGACY_CONVERSATION_ID) return;
    const session = getConversationSessions(this.snapshot.profile.id).find((item) => item.id === sessionId);
    if (!session) return;
    const title = window.prompt("Nombre de la conversación", session.title)?.trim().slice(0, 60);
    if (!title || title === session.title) return;
    updateConversationSession(this.snapshot.profile.id, sessionId, { title });
    this.render();
  }

  private togglePinnedConversation(sessionId: string | undefined): void {
    if (!this.snapshot || !sessionId || sessionId === LEGACY_CONVERSATION_ID) return;
    const session = getConversationSessions(this.snapshot.profile.id).find((item) => item.id === sessionId);
    if (!session) return;
    updateConversationSession(this.snapshot.profile.id, sessionId, { pinned: !session.pinned });
    this.render();
  }

  private async deleteHistoryConversation(sessionId: string | undefined): Promise<void> {
    if (!this.snapshot || !sessionId) return;
    const messages = this.messagesForSession(sessionId);
    if (!window.confirm("Esta conversación y sus mensajes se eliminarán de forma permanente. ¿Deseas continuar?")) return;
    try {
      if (messages.length > 0) await deleteOperationalMessages(this.snapshot.profile.id, messages.map((message) => message.id));
      if (sessionId !== LEGACY_CONVERSATION_ID) removeConversationSession(this.snapshot.profile.id, sessionId);
      const deletedIds = new Set(messages.map((message) => message.id));
      this.snapshot = { ...this.snapshot, messages: this.snapshot.messages.filter((message) => !deletedIds.has(message.id)) };
      if (this.activeConversationId === sessionId) {
        const remaining = getConversationSessions(this.snapshot.profile.id);
        this.activeConversationId = remaining.at(-1)?.id ?? LEGACY_CONVERSATION_ID;
        setActiveConversationId(this.snapshot.profile.id, this.activeConversationId);
      }
      this.syncConversationWindow(this.snapshot.profile.id);
      this.render();
      showToast("Conversación eliminada.", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "No se pudo eliminar la conversación.", "error");
    }
  }

  private focusComposer(): void {
    requestAnimationFrame(() => {
      const input = this.container.querySelector<HTMLTextAreaElement>("#opai-message-input");
      if (!input) return;
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
      this.chat.updateSendButtonState();
    });
  }

  private handleComposerBlur(event: FocusEvent): void {
    if (event.target instanceof HTMLTextAreaElement) this.chat.captureComposerDraft();
  }

  private handleComposerInput(event: Event): void {
    const input = event.target as HTMLElement;
    if (input.id !== "opai-message-input" || !(input instanceof HTMLTextAreaElement)) return;
    this.chat.draftText = input.value;
    const wrapper = this.container.querySelector<HTMLElement>(".opai-input-wrapper");
    const wasExpanded = wrapper?.classList.contains("is-expanded") ?? false;
    input.style.height = "auto";
    const height = Math.min(input.scrollHeight, 144);
    input.style.height = `${height}px`;
    input.style.overflowY = input.scrollHeight > 144 ? "auto" : "hidden";
    const textLength = input.value.trim().length;
    const shouldExpand = height > 44 || (wasExpanded && textLength >= 32);
    wrapper?.classList.toggle("is-expanded", shouldExpand);
    if (!this.processing) this.chat.mascot.setState(textLength > 0 ? "typing" : "idle");
    this.chat.mascot.scheduleSleep();
    this.chat.updateSendButtonState();
  }

  private handleComposerKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target.id !== "opai-message-input") return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void this.sendMessage();
    }
  }

  private handleFileInputChange(event: Event): void {
    const fileInput = event.target as HTMLElement;
    if (fileInput.id !== "opai-file-input" || !(fileInput instanceof HTMLInputElement)) return;
    const selectedFiles = Array.from(fileInput.files ?? []);
    const availableSlots = Math.max(0, 5 - this.chat.pendingFiles.length);
    this.chat.pendingFiles.push(...selectedFiles.slice(0, availableSlots));
    if (selectedFiles.length > availableSlots) showToast("Puedes adjuntar hasta 5 archivos por mensaje.", "warning");
    fileInput.value = "";
    this.chat.mascot.setState("typing");
    this.chat.refreshAttachmentPreviews();
  }

  private async copyMessageText(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      showToast("Texto copiado al portapapeles", "success");
    } catch {
      showToast("No se pudo copiar el texto.", "error");
    }
  }

  private async sendMessage(): Promise<void> {
    if (!this.snapshot || this.processing) return;
    const input = this.container.querySelector<HTMLTextAreaElement>("#opai-message-input");
    if (!input?.value.trim() && this.chat.pendingFiles.length === 0) return;
    const workspaceId = this.snapshot.profile.id;
    this.processing = true;
    this.chat.mascot.setState("thinking");
    const result = await this.chat.sendMessage(this.snapshot, this.snapshot.profile.currentPhase, input, () => this.render());
    this.snapshot = result.snapshot;
    this.processing = false;
    this.chat.mascot.setState(result.responded ? "celebrating" : "idle");
    this.render();
    if (result.responded) { this.chat.mascot.playResponse(); emitQuetzalitoDataUpdated(workspaceId); }
  }

  private async saveEditedMessage(messageId: string): Promise<void> {
    if (!this.snapshot || this.processing) return;
    const textarea = this.container.querySelector<HTMLTextAreaElement>(`[data-edit-msg-input="${CSS.escape(messageId)}"]`);
    const newText = textarea?.value.trim() ?? "";
    if (!newText) { showToast("El mensaje no puede estar vacío.", "warning"); return; }
    const workspaceId = this.snapshot.profile.id;
    this.processing = true;
    this.chat.mascot.setState("thinking");
    const result = await this.chat.saveEditedMessage(this.snapshot, this.snapshot.profile.currentPhase, messageId, newText, () => this.render());
    if (result) this.snapshot = result.snapshot;
    this.processing = false;
    this.chat.mascot.setState(result?.responded ? "celebrating" : "idle");
    this.render();
    if (result?.responded) { this.chat.mascot.playResponse(); emitQuetzalitoDataUpdated(workspaceId); }
  }
}
